<template>
  <v-app :style="bgStyle" class="mx-0">
    <GlobalSnackbar/>

    <top-toolbar v-if="settingsStore.set && userStore.logged_in && router.currentRoute.value.name != 'preview'"/>
    <v-container v-if="settingsStore.set || router.currentRoute.value.name == 'login'" class="pa-0 desktop" :class="{ 'top-buffer': router.currentRoute.value.name != 'login' }">
      <v-row no-gutters>
        <v-col class="pa-0" cols="12">

          <search-header v-if="settingsStore.settings.header == 1 && router.currentRoute.value.name != 'preview'"/>
          <home-branding v-if="settingsStore.settings.header == 2 && router.currentRoute.value.meta.showBrandBar && router.currentRoute.value.name != 'preview'"/>

          <page-header v-if="router.currentRoute.value.meta.showTitleBar && router.currentRoute.value.name != 'preview'" class="mt-2"></page-header>

          <v-main style="padding-top: 0px;">

            <v-slide-x-transition mode="out-in">
              <router-view/>
            </v-slide-x-transition>

          </v-main>
        </v-col>
      </v-row>
      <!-- download-popup v-if="Capacitor.getPlatform() == 'web'" /-->
    </v-container>

    <!--    <splash-screen v-else/>-->

    <v-overlay :model-value="settingsStore.loading" class="align-center justify-center" color="deep-purple-accent-4">
      <v-progress-circular color="primary" indeterminate size="64"></v-progress-circular>
    </v-overlay>

    <v-overlay :model-value="settingsStore.error" class="align-center justify-center" color="red">
      <v-card class="pa-5">
        <v-card-title class="text-center">Error</v-card-title>
        <v-card-text class="text-center">{{ settingsStore.error_message }}</v-card-text>
        <v-card-actions class="justify-center">
          <v-btn color="primary" @click="settingsStore.error = false">Close</v-btn>
        </v-card-actions>
      </v-card>
    </v-overlay>
  </v-app>
</template>

<script lang="ts" setup>
import {computed, onMounted, watch} from 'vue';
import {useRouter} from 'vue-router';
import TopToolbar from './components/Layout/TopToolbar.vue';
import SearchHeader from '@/components/Layout/SearchHeader.vue';
import HomeBranding from './views/HomeComponents/HomeBranding.vue';
import SplashScreen from './views/HomeComponents/SplashScreen.vue';
import PageHeader from '@/components/Layout/PageHeader.vue';

import {useSettingsStore} from '@/api/store/settings';
import {useUserStore} from './api/store/user';

import {appService} from '@/api/services/app';

import {Capacitor} from '@capacitor/core';
import {performImmediateUpdate} from '@/plugins/updater';

import {NativeBiometric} from "capacitor-native-biometric";

import DownloadPopup from '@/components/custom/DownloadPopup.vue';
import {setUser} from "@/api/services/auth";
import GlobalSnackbar from "@/components/GlobalSnackbar.vue";
import {hexToRgba} from "@/tools/design";

const router = useRouter();

const performBiometricVerification = async () => {
  const result = await NativeBiometric.isAvailable();

  if (!result.isAvailable) return;

  const verified = await NativeBiometric.verifyIdentity({
    reason: "Secure Login",
    title: "Log in",
    subtitle: "We just need to verify your identity.",
    description: "Please use your biometric authentication method to log in.",
  })
      .then(() => true)
      .catch(() => false);

  if (!verified) return false;

  return true;
}

const settingsStore = useSettingsStore();
const userStore = useUserStore();

const appStart = async () => {

  console.log("App Starting");

  if (Capacitor.getPlatform() == "web") {
    console.log("Running on Web Mode");
  } else {
    console.log("Running on Mobile");
    console.log("Checking for updates");
    performImmediateUpdate();
  }

  // Skip fetching if settings are already loaded (e.g., from login)
  if (settingsStore.set) {
    console.log("Settings already loaded, skipping fetch");
    updateCSSVariables();
    return;
  }

  if (userStore.logged_in && (Capacitor.getPlatform() == "web" || await performBiometricVerification())) {
    console.log("User Already Logged In");
    console.log("Fetching App Configuration");

    await appService.fetchDisplay().then((response: any) => {

      userStore.setUser(response.client, response.clinicAvatar);
      setUser(response.client);

      if (!response.app.settings || response.app.settings == "null") {
        settingsStore.set = true; // Set even if no settings
        return;
      }

      const data = JSON.parse(response.app.settings);

      if (response.app.image && response.app.image.file && response.app.image.file.file_path) {
        data.brandingImg = response.app.image.file.file_path;
        //console.log("Branding Image: ", settingsStore.settings.brandingImg);
      }

      // Use store action to update settings (maintains reactivity)
      settingsStore.updateSettings(data);
      updateCSSVariables();

    }).catch((err: any) => {
      console.log("Display Error: ", err);
      if (err.response?.status === 401) {
        router.push({name: 'login'});
      }
      // Set to true even on error so UI can render
      settingsStore.set = true;
    });
  } else {
    // Not logged in, use default settings
    settingsStore.set = true;
  }
};

// Update CSS variables globally based on the settings
const updateCSSVariables = () => {
  const primaryColor = settingsStore.settings.primaryColor;
  const buttonColor = settingsStore.settings.buttonColor;
  const buttonColor2 = settingsStore.settings.buttonColor2;

  document.documentElement.style.setProperty('--primary-color', primaryColor);
  document.documentElement.style.setProperty('--primary-button-color', buttonColor);
  document.documentElement.style.setProperty('--secondary-button-color', buttonColor2);
};

onMounted(() => {
  router.isReady().then(() => {
    if (router.currentRoute.value.name && router.currentRoute.value.name !== 'preview') {
      console.log("Route: ", router.currentRoute.value.name);
      appStart();
    } else {
      console.log("Route: ", router.currentRoute.value.name);
      settingsStore.set = true;
    }
  });
  updateCSSVariables();
});

const bgStyle = computed(() => {
  if (!settingsStore.settings) return 'background-color: rgba(255, 255, 255, 0.3);'; // 30% opacity
  if (router.currentRoute.value.meta.showBrandBar || router.currentRoute.value.meta.withSettingsBackground) {
    return 'background: linear-gradient(180deg, ' + hexToRgba(settingsStore.settings.primaryColor, 0.3) + '0, ' + 'rgba(255, 255, 255, 0.3)' + ' 50% 100%);';
  }
  return 'background-color: rgba(255, 255, 255, 0.3);';
});



watch(() => settingsStore.settings, () => {
  updateCSSVariables();
}, { deep: true })

</script>

<style lang="scss">
@import 'assets/scss/styles.scss';

.primary-color {
  color: var(--primary-color);
}

.desktop {
  max-width: 480px;
  margin: 0 auto;
}

.top-buffer {
  margin-top: 40px;
}

.layout1-bg {
  background-color: white;
}

.number-control {
  margin-left: 0;
  margin-right: 0;
  width: 120px;
  text-align: center;
}

.number-control-minus {
  margin: 0;
  padding: 0;
}

.number-control-plus {
  margin: 0;
  padding: 0;
}
</style>
